import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "../../shared/ui/button.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { AnalysisChart } from "./analysis-chart.js";
import { AnalysisContext } from "./analysis-context.js";
import {
  useAnalysisStatus,
  useAnalysisMonitors,
  useAnalysisHistory,
  useAnalysisReport,
  useAnalysisMutation,
  type MonitorInput,
  type AnalysisMonitor,
  type AnalysisReport,
} from "./queries.js";

const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const blankMonitor: MonitorInput = {
  symbol: "",
  frequency: "weekly",
  weekDay: 5,
  time: "16:30",
  enabled: true,
};
function monitorInput(monitor: AnalysisMonitor): MonitorInput {
  return {
    symbol: monitor.symbol,
    frequency: monitor.frequency,
    weekDay: monitor.weekDay,
    time: monitor.time,
    enabled: monitor.enabled,
  };
}
function timestamp(value: string | null | undefined): string {
  return value == null
    ? "—"
    : new Date(value).toLocaleString(undefined, {
        timeZone: "America/New_York",
        timeZoneName: "short",
      });
}
function money(value: number): string {
  return `$${value.toFixed(4)}`;
}
function action(value: string | undefined): string {
  return value === "EXIT" ? "SELL / EXIT" : (value?.replaceAll("_", " ") ?? "—");
}
type HistoryDeletion =
  | { kind: "report"; report: AnalysisReport }
  | { kind: "history"; monitorId: string; symbol: string; count: number; createdBefore: string };

export function AiAnalysisPage() {
  const status = useAnalysisStatus();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const monitors = useAnalysisMonitors(includeDeleted);
  const mutation = useAnalysisMutation();
  const [editing, setEditing] = useState<{ id?: string; input: MonitorInput } | null>(null);
  const [monitorId, setMonitorId] = useState("");
  const [cursor, setCursor] = useState("");
  const [reportId, setReportId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [historyDeletion, setHistoryDeletion] = useState<HistoryDeletion | null>(null);
  const [notice, setNotice] = useState("");
  const history = useAnalysisHistory(monitorId, cursor);
  const report = useAnalysisReport(reportId);
  const pending = mutation.isPending;
  const selectedMonitor = monitors.data?.items.find((item) => item.id === monitorId);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (editing === null) return;
    mutation.mutate(
      {
        action: "save",
        body: { ...editing.input, symbol: editing.input.symbol.trim().toUpperCase() },
        ...(editing.id === undefined ? {} : { id: editing.id }),
      },
      {
        onSuccess: () => {
          setEditing(null);
          setNotice("Monitor saved.");
        },
      },
    );
  };
  const showHistory = (id: string) => {
    setHistoryDeletion(null);
    mutation.reset();
    setMonitorId(id);
    setCursor("");
    setReportId("");
  };
  const confirmHistoryDeletion = () => {
    if (historyDeletion === null) return;
    const deletion = historyDeletion;
    mutation.mutate(
      deletion.kind === "report"
        ? { action: "delete-report", id: deletion.report.id }
        : {
            action: "clear-history",
            id: deletion.monitorId,
            createdBefore: deletion.createdBefore,
          },
      {
        onSuccess: (result) => {
          setHistoryDeletion(null);
          setCursor("");
          if (deletion.kind === "history" || reportId === deletion.report.id) setReportId("");
          const count = "deletedCount" in result ? result.deletedCount : 1;
          setNotice(
            `${count} analysis report${count === 1 ? "" : "s"} deleted. Schedule and budget usage are unchanged.`,
          );
        },
      },
    );
  };

  return (
    <section className="page-stack page-stack--compact" aria-label="AI Analysis">
      <div className="page-toolbar">
        <h1>AI Analysis</h1>
        <div className="button-row">
          <RefreshButton
            queries={[
              status,
              monitors,
              ...(monitorId === "" ? [] : [history]),
              ...(reportId === "" ? [] : [report]),
            ]}
          />
          <Button
            onClick={() => {
              mutation.reset();
              setEditing({ input: { ...blankMonitor } });
            }}
          >
            Add Monitor
          </Button>
        </div>
      </div>
      <p className="section-description">
        Scheduled stock research with conditional entry and holding recommendations. Analysis never
        places orders.
      </p>
      <label className="check-field">
        <input
          type="checkbox"
          checked={includeDeleted}
          onChange={(event) => setIncludeDeleted(event.currentTarget.checked)}
        />
        <span>Include deleted schedules</span>
      </label>
      {status.data === undefined ? (
        status.isError ? (
          <p role="alert">{status.error.message}</p>
        ) : (
          <p role="status">Loading analysis configuration…</p>
        )
      ) : (
        <div className="content-card ai-analysis-budget">
          <div>
            <strong>{status.data.enabled ? status.data.model : "Analysis is disabled"}</strong>
            <p className="section-description">
              {status.data.enabled
                ? "Market, company and economic research · 5–20 trading-day horizon"
                : "An administrator can configure AI analysis in Admin. A server API key, model, pricing and positive monthly budget are required."}
            </p>
          </div>
          <div>
            <strong>
              {money(status.data.usedBudgetUsd)} / ${status.data.monthlyBudgetUsd.toFixed(2)}
            </strong>
            <p className="section-description">
              {status.data.budgetMonth} UTC monthly budget · includes reserved costs
            </p>
            <small>
              Maximum reservation per analysis: {money(status.data.maxCostPerAnalysisUsd)}
            </small>
          </div>
        </div>
      )}
      {mutation.error === null ? null : (
        <p className="form-message--error" role="alert">
          {mutation.error.message}
        </p>
      )}
      {notice === "" ? null : <p role="status">{notice}</p>}
      {editing === null ? null : (
        <form className="content-card form-stack" onSubmit={submit} aria-label="Monitor schedule">
          <h2>{editing.id === undefined ? "Add stock monitor" : "Edit stock monitor"}</h2>
          <div className="field-grid ai-analysis-fields">
            <div className="field-stack">
              <label htmlFor="ai-symbol">Stock symbol</label>
              <input
                id="ai-symbol"
                required
                autoFocus
                maxLength={15}
                pattern="[A-Za-z][A-Za-z0-9.\-]{0,14}"
                placeholder="AAPL"
                value={editing.input.symbol}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    input: { ...editing.input, symbol: event.currentTarget.value.toUpperCase() },
                  })
                }
              />
            </div>
            <div className="field-stack">
              <label htmlFor="ai-frequency">Frequency</label>
              <select
                id="ai-frequency"
                value={editing.input.frequency}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    input: {
                      ...editing.input,
                      frequency: event.currentTarget.value as MonitorInput["frequency"],
                    },
                  })
                }
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {editing.input.frequency !== "weekly" ? null : (
              <div className="field-stack">
                <label htmlFor="ai-weekday">Analysis day</label>
                <select
                  id="ai-weekday"
                  value={editing.input.weekDay}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      input: { ...editing.input, weekDay: Number(event.currentTarget.value) },
                    })
                  }
                >
                  {weekdays.map((day, index) => (
                    <option key={day} value={index + 1}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field-stack">
              <label htmlFor="ai-time">Time (New York)</label>
              <input
                type="time"
                id="ai-time"
                required
                min="16:15"
                max="22:59"
                value={editing.input.time}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    input: { ...editing.input, time: event.currentTarget.value },
                  })
                }
              />
            </div>
          </div>
          <p className="field-hint">
            Daily runs follow trading sessions. Weekly holidays use the preceding session in that
            week. Monthly runs use the last trading session. All schedules use New York time.
          </p>
          <label className="check-field">
            <input
              type="checkbox"
              checked={editing.input.enabled}
              onChange={(event) =>
                setEditing({
                  ...editing,
                  input: { ...editing.input, enabled: event.currentTarget.checked },
                })
              }
            />
            <span>Schedule enabled</span>
          </label>
          <div className="button-row">
            <Button type="submit" disabled={pending}>
              {pending ? "Saving…" : "Save Monitor"}
            </Button>
            <Button type="button" tone="quiet" disabled={pending} onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {monitors.isPending ? (
        <ResourceState
          kind="loading"
          title="Loading monitors"
          description="Retrieving your stock analysis schedules."
        />
      ) : monitors.isError ? (
        <ResourceState
          kind="error"
          title="Monitors unavailable"
          description={monitors.error.message}
        />
      ) : monitors.data?.items.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No stock monitors yet"
          description="Add a symbol and schedule to begin collecting AI analysis reports."
        />
      ) : (
        <div className="content-card ai-analysis-table">
          <table>
            <caption>Stock monitor schedules · New York time</caption>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Schedule</th>
                <th>Latest analysis</th>
                <th>Next analysis</th>
                <th>If not owned</th>
                <th>If owned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {monitors.data?.items.map((monitor) => (
                <tr key={monitor.id}>
                  <th scope="row">
                    {monitor.symbol}
                    <br />
                    <span className="status-pill">
                      {monitor.deleted === true ? "Deleted" : monitor.enabled ? "Active" : "Paused"}
                    </span>
                  </th>
                  <td data-label="Schedule">
                    {monitor.frequency === "weekly"
                      ? `Weekly · ${weekdays[monitor.weekDay - 1]}`
                      : monitor.frequency === "monthly"
                        ? "Monthly · last session"
                        : "Daily · trading sessions"}
                    <br />
                    <small>{monitor.time} Eastern</small>
                  </td>
                  <td data-label="Latest analysis">
                    {timestamp(
                      monitor.latestReport?.completedAt ?? monitor.latestReport?.createdAt,
                    )}
                    <br />
                    <small>
                      {monitor.latestReport?.status.replaceAll("_", " ") ?? "No reports"}
                    </small>
                  </td>
                  <td data-label="Next analysis">
                    {monitor.enabled ? timestamp(monitor.nextAnalysisAt) : "Paused"}
                  </td>
                  <td data-label="If not owned">
                    <span className="status-pill">
                      {action(monitor.latestReport?.decision?.entryAction)}
                    </span>
                  </td>
                  <td data-label="If owned">
                    <span className="status-pill">
                      {action(monitor.latestReport?.decision?.holdingAction)}
                    </span>
                  </td>
                  <td data-label="Actions">
                    <div className="button-row">
                      <Button
                        tone="quiet"
                        disabled={
                          monitor.deleted === true ||
                          pending ||
                          status.data?.enabled !== true ||
                          monitor.latestReport?.status === "queued" ||
                          monitor.latestReport?.status === "running"
                        }
                        onClick={() =>
                          mutation.mutate(
                            { action: "run", id: monitor.id },
                            {
                              onSuccess: () => {
                                showHistory(monitor.id);
                                setNotice(
                                  `${monitor.symbol} analysis queued. Results appear when the worker completes.`,
                                );
                              },
                            },
                          )
                        }
                      >
                        Run Now
                      </Button>
                      <Button tone="quiet" onClick={() => showHistory(monitor.id)}>
                        View History
                      </Button>
                      <Button
                        tone="quiet"
                        disabled={pending || monitor.deleted === true}
                        onClick={() => {
                          mutation.reset();
                          setEditing({ id: monitor.id, input: monitorInput(monitor) });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        tone="quiet"
                        disabled={pending || monitor.deleted === true}
                        onClick={() =>
                          mutation.mutate({
                            action: "save",
                            id: monitor.id,
                            body: { ...monitorInput(monitor), enabled: !monitor.enabled },
                          })
                        }
                      >
                        {monitor.enabled ? "Pause" : "Resume"}
                      </Button>
                      <Button
                        tone="danger"
                        disabled={pending || monitor.deleted === true}
                        onClick={() => setDeletingId(monitor.id)}
                      >
                        Delete schedule
                      </Button>
                    </div>
                    {deletingId !== monitor.id ? null : (
                      <div className="ai-analysis-delete">
                        <p>Delete this schedule? Existing reports are retained.</p>
                        <div className="button-row">
                          <Button
                            tone="danger"
                            disabled={pending}
                            onClick={() =>
                              mutation.mutate(
                                { action: "delete", id: monitor.id },
                                {
                                  onSuccess: () => {
                                    setDeletingId("");
                                    if (monitorId === monitor.id) setMonitorId("");
                                    setNotice("Monitor deleted. Saved reports are retained.");
                                  },
                                },
                              )
                            }
                          >
                            Confirm delete
                          </Button>
                          <Button tone="quiet" onClick={() => setDeletingId("")}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {monitorId === "" ? null : (
        <section className="content-card section-stack" aria-label="Analysis history">
          <div className="page-toolbar">
            <h2>{selectedMonitor?.symbol ?? "Stock"} analysis history</h2>
            <Button
              tone="danger"
              disabled={
                pending ||
                history.isError ||
                history.isFetching ||
                (history.data?.deletableCount ?? 0) === 0
              }
              onClick={() => {
                if (history.data === undefined) return;
                mutation.reset();
                setHistoryDeletion({
                  kind: "history",
                  monitorId,
                  symbol: selectedMonitor?.symbol ?? "Stock",
                  count: history.data.deletableCount,
                  createdBefore: history.data.clearBefore,
                });
              }}
            >
              Clear history for this symbol
            </Button>
          </div>
          {history.data === undefined ? null : (
            <p className="field-hint">
              {history.data.reportCount} reports · {history.data.deletableCount} finished reports
              can be deleted. Queued and running analyses are retained.
            </p>
          )}
          {historyDeletion === null ? null : (
            <HistoryDeleteDialog pending={pending} onCancel={() => setHistoryDeletion(null)}>
              <h3 id="history-delete-title">
                {historyDeletion.kind === "report"
                  ? "Delete analysis report?"
                  : `Clear ${historyDeletion.symbol} analysis history?`}
              </h3>
              <p id="history-delete-description">
                {historyDeletion.kind === "report"
                  ? `Delete the ${historyDeletion.report.symbol} report from ${timestamp(historyDeletion.report.createdAt)}?`
                  : `Delete ${historyDeletion.count} finished report${historyDeletion.count === 1 ? "" : "s"} for ${historyDeletion.symbol} across all history pages? Reports created or finished after this preview are kept.`}{" "}
                This cannot be undone. The schedule and budget usage will remain unchanged.
              </p>
              {mutation.error === null ? null : <p role="alert">{mutation.error.message}</p>}
              <div className="button-row">
                <Button
                  tone="quiet"
                  autoFocus
                  disabled={pending}
                  onClick={() => setHistoryDeletion(null)}
                >
                  Cancel
                </Button>
                <Button tone="danger" disabled={pending} onClick={confirmHistoryDeletion}>
                  {pending
                    ? "Deleting…"
                    : historyDeletion.kind === "report"
                      ? "Confirm delete report"
                      : "Confirm clear history"}
                </Button>
              </div>
            </HistoryDeleteDialog>
          )}
          {history.isPending ? (
            <p role="status">Loading reports…</p>
          ) : history.isError ? (
            <p role="alert">{history.error.message}</p>
          ) : history.data?.items.length === 0 ? (
            <p>No reports yet. Use Run Now or wait for the scheduled analysis.</p>
          ) : (
            <div className="ai-analysis-history">
              {history.data?.items.map((item) => (
                <div className="ai-analysis-history-card" key={item.id}>
                  <button
                    className={`ai-analysis-history-item${item.id === reportId ? " ai-analysis-history-item--selected" : ""}`}
                    disabled={pending}
                    onClick={() => setReportId(item.id)}
                  >
                    <strong>{timestamp(item.createdAt)}</strong>
                    <span>
                      {item.status.replaceAll("_", " ")} · {item.trigger}
                    </span>
                    <span>
                      Entry: {action(item.decision?.entryAction)} · Holding:{" "}
                      {action(item.decision?.holdingAction)}
                    </span>
                    <small>
                      {item.costUsd === undefined ? "" : `API cost ${money(item.costUsd)}`}
                    </small>
                  </button>
                  <details className="ai-analysis-report-menu">
                    <summary
                      aria-label={`Report actions for ${item.symbol} ${timestamp(item.createdAt)}`}
                    >
                      ⋯
                    </summary>
                    <Button
                      tone="danger"
                      disabled={pending || item.status === "queued" || item.status === "running"}
                      onClick={() => {
                        mutation.reset();
                        setHistoryDeletion({ kind: "report", report: item });
                      }}
                    >
                      Delete report
                    </Button>
                    {item.status === "queued" || item.status === "running" ? (
                      <small>Available after analysis finishes.</small>
                    ) : null}
                  </details>
                </div>
              ))}
            </div>
          )}
          <div className="button-row">
            {cursor === "" ? null : (
              <Button tone="quiet" onClick={() => setCursor("")}>
                Newest reports
              </Button>
            )}
            {history.data?.nextCursor == null ? null : (
              <Button tone="quiet" onClick={() => setCursor(history.data?.nextCursor ?? "")}>
                Older reports
              </Button>
            )}
          </div>
          {reportId === "" ? (
            <p className="field-hint">Select a report to inspect its reasoning and evidence.</p>
          ) : report.isPending ? (
            <p role="status">Loading report…</p>
          ) : report.isError ? (
            <p role="alert">{report.error.message}</p>
          ) : report.data === undefined ? null : (
            <ReportDetail report={report.data} />
          )}
        </section>
      )}
    </section>
  );
}

function HistoryDeleteDialog({
  children,
  pending,
  onCancel,
}: {
  readonly children: ReactNode;
  readonly pending: boolean;
  readonly onCancel: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return (
    <dialog
      ref={dialogRef}
      className="ai-analysis-history-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="history-delete-title"
      aria-describedby="history-delete-description"
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onCancel();
      }}
    >
      <div className="section-stack">{children}</div>
    </dialog>
  );
}

function ReportDetail({ report }: { readonly report: AnalysisReport }) {
  const decision = report.decision;
  return (
    <article className="section-stack" aria-label="Analysis report">
      <h3>{report.symbol} report</h3>
      <p className="section-description">
        {report.status.replaceAll("_", " ")} · Scheduled: {timestamp(report.scheduledAt)} · Started:{" "}
        {timestamp(report.startedAt)}
      </p>
      {report.error === undefined ? null : (
        <p className="form-message--error" role="alert">
          {report.error}
        </p>
      )}
      {decision === undefined ? null : (
        <>
          <div className="ai-analysis-decisions">
            <div>
              <span>If you do not own {report.symbol}</span>
              <strong>{action(decision.entryAction)}</strong>
            </div>
            <div>
              <span>If you already own {report.symbol}</span>
              <strong>{action(decision.holdingAction)}</strong>
            </div>
            <div>
              <span>Expected horizon</span>
              <strong>{decision.horizonTradingDays} trading days</strong>
            </div>
          </div>
          {report.status === "completed" ? <AnalysisChart key={report.id} report={report} /> : null}
          <p className="ai-analysis-reason">{decision.reason}</p>
          <AnalysisContext report={report} />
          <div className="ai-analysis-factors">
            {(
              [
                ["Bullish factors", decision.bullishFactors],
                ["Bearish factors", decision.bearishFactors],
                ["Invalidation conditions", decision.invalidationConditions],
              ] as const
            ).map(([title, items]) => (
              <section key={title}>
                <h3>{title}</h3>
                {items.length === 0 ? (
                  <p>None identified.</p>
                ) : (
                  <ul>
                    {items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
          <p className="field-hint">Referenced evidence: {decision.evidenceIds.join(", ")}</p>
        </>
      )}
      {report.snapshot === undefined ? null : (
        <>
          <p className="field-hint">
            Analysis time: {timestamp(report.snapshot.asOf)} · Latest completed price session:{" "}
            {report.snapshot.lastSessionDate}
          </p>
          <ul className="field-hint">
            {report.snapshot.limitations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <details>
            <summary>Evidence snapshot</summary>
            <div className="ai-analysis-evidence">
              {report.snapshot.evidence.map((item) => (
                <div key={item.id}>
                  <strong>{item.id}</strong>
                  <p>{item.description}</p>
                  <dl>
                    {Object.entries(item.values).map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>
                          {Number.isInteger(value) ? value.toLocaleString() : value.toFixed(4)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </details>
        </>
      )}
      <p className="field-hint">
        Model: {report.model ?? "—"} · Prompt: {report.promptVersion ?? "—"} · Tokens:{" "}
        {report.inputTokens ?? "—"} input / {report.outputTokens ?? "—"} output · Cost:{" "}
        {report.costUsd === undefined ? "—" : money(report.costUsd)}
        {report.costUsd === undefined && report.reservedCostUsd !== undefined
          ? ` · Reserved: ${money(report.reservedCostUsd)}`
          : ""}
      </p>
    </article>
  );
}
